import React, { useEffect, useMemo, useRef } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormGroup,
  FormControlLabel,
  RadioGroup,
  FormControl,
  Radio,
  Typography,
  Divider,
  Grid,
  CircularProgress,
  TextField,
} from "@mui/material";
import { t } from "i18next";
import * as f from "../../utils/formatter";

import AutocompleteSelect from "../AutocompleteSelect";
import { ClientAutocomplete } from "./ClientAutocomplete";
import { useProductOptions } from "./hooks/useProductOptions";
import { useServiceOptions } from "./hooks/useServiceOptions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowsRotate,
  faCalendarPlus,
  faPlus,
  faRotateLeft,
  faXmark,
  faCalendarLinesPen,
} from "@fortawesome/pro-solid-svg-icons";
import * as api from "../../services/api";
import { useSuggestions } from "./hooks/useSuggestions";
import { getStartDateFromNote, stablePaginateSuggestions } from "./utils";
import { RESERVATION_MODES } from "./ReservationDialog";
import * as doc from "./doctor.mjs";
import DoctorsInput from "./DoctorsInput";
import { useLatest } from "../../hooks";
import DoctorIcon from "./DoctorIcon";
import { isOverlap } from "shared/src/util.mjs";
import { status } from "shared/src/appointment.mjs";

/**
 * @typedef OptionsProperties
 * @property {Array} options - Array of options.
 * @property {boolean} loading - Indicates if options are loading.
 */

/**
 * @typedef ClientOptionsProperties
 * @property {Array} options - Array of options.
 * @property {boolean} loading - Indicates if options are loading.
 * @property {function} search - Function to search for clients.
 */

/**
 * @typedef DefaultOptions
 * @property {Object} client
 * @property {Object} location
 * @property {Object} product
 * @property {Object} service
 * @property {Object} doctor
 */

function useClientData(
  selectedClient,
  selectedLocation,
  setReservationSettingState,
) {
  const [state, setState] = React.useState({
    loading: false,
    note: null,
    oldAppointment: null,
  });

  const clientId = selectedClient?.id;
  const locationId = selectedLocation?.id ?? selectedClient?.location_id;

  useLatest(
    async (signal) => {
      if (!clientId) {
        setState({ note: null, oldAppointment: null, loading: false });
        return;
      }

      try {
        setState({ note: null, oldAppointment: null, loading: true });

        const data = await api.loadClientReservationData(
          {
            page: {
              limit: 1,
              offset: 0,
            },
            notesFilter: {
              client_id: { eq: clientId },
              note_type: { eq: "receptionist" },
              active: { eq: true },
            },
            notesOrder: {
              desc: "created_at",
            },
            appointmentsFilter: {
              client_id: { eq: clientId },
              status_id: { in: [status.TREATED, status.FINISHED] },
            },
            appointmentsOrder: { desc: "treated" },
          },
          { signal },
          api.contextFromLocation(locationId),
        );

        const latest = data?.data?.appointmentNotes2?.data[0];
        const last = data?.data?.appointments?.data[0];

        const isActionable = (v) => {
          if (!v) return false;
          if (!v.service_id) return false;

          if (v.reservation_unit === "earliestAvailableDate") return true;

          const count = Number(v.reservation_count);
          return !!v.reservation_unit && Number.isFinite(count) && count > 0;
        };

        const actionable = isActionable(latest) ? latest : null;

        setState({
          note: actionable,
          oldAppointment: last,
          loading: false,
        });

        const newReservationSettings = {};
        if (actionable) {
          newReservationSettings.calendarRange = [
            getStartDateFromNote(actionable),
            getStartDateFromNote(actionable).add(7, "day").hour(23),
          ];
        }

        setReservationSettingState((setting) => ({
          ...setting,
          ...newReservationSettings,
          note: actionable,
          oldAppointment: last,
          anyClientHasNote: setting.anyClientHasNote || actionable != null,
        }));
      } finally {
        if (!signal.aborted) {
          setState((prev) => ({ ...prev, loading: false }));
        }
      }
    },
    [clientId, locationId, setReservationSettingState],
  );

  return state;
}

function suggestionsOverlap(s1, s2) {
  if (s1.calendar_id !== s2.calendar_id) {
    return false;
  }

  return isOverlap(s1.start, s1.end, s2.start, s2.end);
}

function removeOverlappingSuggestions(suggestions, selectedClients, index) {
  const prohibitedSuggestions = selectedClients
    .map((sc, i) => (i !== index && sc.selectedSuggestion) || null)
    .filter((s) => s != null);

  if (prohibitedSuggestions.length === 0) {
    return suggestions;
  }

  return suggestions.filter(
    (s) => !prohibitedSuggestions.some((ps) => suggestionsOverlap(ps, s)),
  );
}

/**
 * ClientReservation component for selecting reservation details.
 * @param {Object} props - Component properties.
 * @param {number} props.index - Index of the client in the reservation.
 * @param {boolean} props.isPrimaryClient - Indicates if the client is primary.
 * @param {DefaultOptions[]} props.defaultOptions - Default options for inputs.
 * @param {ClientOptionsProperties} props.clientOptions - Shared client options across panels.
 * @param {OptionsProperties} props.locationOptions - Shared location options across panels.
 * @param {OptionsProperties} props.doctorOptions - Shared doctor options based on the primary location.
 * @param {Object} props.reservationSettingState - Reservation settings.
 * @param {function} props.setReservationSettingState - Function to set reservation settings.
 * @param {Array} props.selectedClients - Array of selected clients.
 * @param {function} props.setSelectedClients - Function to set selected clients.
 * @param {function=} props.onPrimarySuggestionsUpdate - Callback to push primary suggestion list up to parent dialog.
 * @param {() => void=} props.openManualDialog - Opens manual selection dialog (primary only)
 * @param {() => void=} props.clearManualSelection - Clears manual selection mode
 * @returns {ReactElement} Rendered component.
 */
export const ClientReservation = ({
  index,
  isPrimaryClient,
  defaultOptions,
  clientOptions,
  locationOptions,
  doctorOptions,
  reservationSettingState,
  setReservationSettingState,
  selectedClients,
  setSelectedClients,
  onPrimarySuggestionsUpdate,
  openManualDialog,
  clearManualSelection,
  doctor_required,
}) => {
  const data = selectedClients[index];

  const setData = React.useCallback(
    (index, m) => {
      setSelectedClients((prev) => {
        const updatedClients = [...prev];
        const client = { ...updatedClients[index] };
        updatedClients[index] = client;

        for (const [k, v] of Object.entries(m)) {
          if (typeof v === "function") {
            client[k] = v(client[k]);
          } else {
            client[k] = v;
          }
        }

        return updatedClients;
      });
    },
    [setSelectedClients],
  );

  const selectedClient = data.selectedClient;
  const setSelectedClient = (val) => setData(index, { selectedClient: val });
  const selectedLocation = data.selectedLocation;
  const setSelectedLocation = (val) =>
    setData(index, { selectedLocation: val });
  const selectedProduct = data.selectedProduct;
  const setSelectedProduct = (val) => setData(index, { selectedProduct: val });
  const selectedService = data.selectedService;
  const setSelectedService = (val) => {
    const updates = { selectedService: val };
    if (val?.requires_doctor) {
      updates.selectedClient = function (prev) {
        return { ...prev, with_selected_doctor_only: true };
      };
    }
    setData(index, updates);
  };
  const selectedDoctors = data.selectedDoctors;
  const setSelectedDoctors = (val) => setData(index, { selectedDoctors: val });

  const {
    note,
    oldAppointment,
    loading: loadingClientData,
  } = useClientData(
    selectedClient,
    selectedLocation,
    setReservationSettingState,
  );
  // Track whether user manually changed the location to prevent later auto overrides
  const userChangedLocationRef = useRef(false);
  // Track manual service change to avoid overriding with note-based prefill afterwards
  const userChangedServiceRef = useRef(false);
  const preferredDoctors = React.useMemo(
    () => doc.getPreferredDoctors(selectedClient, doctorOptions.options),
    [
      selectedClient?.any_doctor,
      selectedClient?.doctors,
      doctorOptions.options,
    ],
  );

  useEffect(() => {
    if (doctorOptions.loading) {
      return;
    }

    if (doc.isEmpty(selectedDoctors)) {
      if (
        (selectedService?.requires_doctor || doctor_required) &&
        doc.isEmpty(preferredDoctors)
      ) {
        setSelectedDoctors(doc.selectAnyDoctor());
      } else {
        setSelectedDoctors(preferredDoctors);
      }
    }
  }, [preferredDoctors, doctor_required, selectedService]);

  const selectedClientId = selectedClient?.id;

  // PRODUCT
  const productFilter = useMemo(() => {
    if (selectedLocation == null || selectedClientId == null) {
      return null;
    }

    return {
      company_id: { eq: selectedLocation.company_id },
      client_id: { eq: selectedClientId },
    };
  }, [selectedLocation, selectedClientId]);
  const productOptions = useProductOptions(productFilter);

  // SERVICE
  const serviceFilter = React.useMemo(() => {
    return selectedLocation == null || selectedProduct == null
      ? null
      : {
          location_id: { eq: selectedLocation.id },
          product_id: { eq: selectedProduct.id },
          create_reservation: { eq: true },
        };
  }, [selectedLocation, selectedProduct]);
  const serviceOptions = useServiceOptions(serviceFilter);

  useEffect(() => {
    const currentAppointment = selectedClients[index]?.currentAppointment;
    if (currentAppointment != null) {
      const service = currentAppointment.service;
      if (selectedProduct == null) {
        const product_id = service?.product_id;
        const product = productOptions.options.find(
          (po) => po.id === product_id,
        );

        if (product != null) {
          setSelectedProduct(product);
        }
      }

      if (
        selectedService == null &&
        selectedProduct?.id === service?.product_id
      ) {
        const s = serviceOptions.options.find((s) => s.id === service?.id);
        if (s != null) {
          setSelectedService(s);
        }
      }
    }

    /** @type {{client?: any, location?: any, product?: any, service?: any, doctor?: any}} */
    const dfl = defaultOptions[index] || {};
    // Only initialize fields that are not chosen yet to avoid overriding manual changes
    if (selectedClient == null && dfl.client) setSelectedClient(dfl.client);
    // Location: mirror primary for non-primary; for primary, only initialize once
    const locFromParent = defaultOptions[0]?.location;
    if (!isPrimaryClient) {
      if (
        locFromParent &&
        (selectedLocation == null || selectedLocation.id !== locFromParent.id)
      ) {
        setSelectedLocation(locFromParent);
      }
    } else if (selectedLocation == null && locFromParent) {
      setSelectedLocation(locFromParent);
    }
  }, [
    defaultOptions,
    index,
    isPrimaryClient,
    selectedClient,
    selectedClients,
    selectedLocation,
    selectedProduct,
    selectedService,
    productOptions,
    serviceOptions,
  ]);

  React.useEffect(() => {
    const currentAppointment = selectedClients[index]?.currentAppointment;
    if (currentAppointment != null) {
      return;
    }

    const service = note?.service;
    if (service == null) {
      if (!loadingClientData && selectedProduct == null) {
        const defaultProduct =
          productOptions.options.find(
            (po) => po.id === "59b7da94-8ce7-4bd1-bdce-f981a72c421e",
          ) ??
          productOptions.options[0] ??
          null;

        if (defaultProduct != null) {
          setSelectedProduct(defaultProduct);
        }
      }

      return;
    }

    if (selectedProduct == null) {
      const product = productOptions.options.find(
        (p) => p.id === service.product_id,
      );
      if (product != null) {
        setSelectedProduct(product);
      }
    }

    if (selectedService == null && selectedProduct?.id === service.product_id) {
      const s = serviceOptions.options.find((s) => s.id === service.id);
      if (s != null) {
        setSelectedService(s);
      }
    }
  }, [
    note,
    productOptions,
    serviceOptions,
    loadingClientData,
    selectedClients,
    index,
  ]);

  // Prefill location:
  // Always use client's default location_id for the initial fill.
  // Never override after the user manually changes the location.
  useEffect(() => {
    if (!locationOptions?.options?.length) return;
    if (!userChangedLocationRef.current) {
      const clientLocId = selectedClient?.location_id;
      if (!clientLocId) return;
      const clientLoc =
        locationOptions.options.find((o) => o.id === clientLocId) || null;
      if (clientLoc) setSelectedLocation(clientLoc);
    }
  }, [locationOptions, selectedClient]);

  const { suggestions, loading: isSuggestionsLoading } = useSuggestions(
    selectedClients,
    index,
    reservationSettingState,
  );

  const nonOverlappingSuggestions = React.useMemo(() => {
    return removeOverlappingSuggestions(suggestions, selectedClients, index);
  }, [suggestions, selectedClients, index]);

  // Notify parent (index 0 acts as source for manual dialog)
  useEffect(() => {
    if (index === 0 && typeof onPrimarySuggestionsUpdate === "function") {
      onPrimarySuggestionsUpdate(suggestions, isSuggestionsLoading);
    }
    setReservationSettingState((s) => ({
      ...s,
      suggestions: suggestions,
      suggestionsLoading: isSuggestionsLoading,
    }));
  }, [suggestions, isSuggestionsLoading, index, onPrimarySuggestionsUpdate]);

  //SUGGESTIONS
  // Stable pages derived from sorted suggestions
  const stablePages = React.useMemo(
    () => stablePaginateSuggestions(nonOverlappingSuggestions, 5),
    [nonOverlappingSuggestions],
  );

  // Current page index is driven by parent (primarySuggestionsPage) for index 0
  const pageIndex =
    index === 0 && reservationSettingState.primarySuggestionsPage != null
      ? reservationSettingState.primarySuggestionsPage
      : 0;

  const currentSuggestions = stablePages[pageIndex] || [];

  const canGoBackEnabled = pageIndex > 0;
  const hasMoreEnabled = pageIndex < stablePages.length - 1;

  const handleNextSuggestions = () => {
    if (!hasMoreEnabled) return;
    if (index === 0) {
      setReservationSettingState((s) => ({
        ...s,
        primarySuggestionsPage: pageIndex + 1,
      }));
    }
  };
  const handleBackSuggestions = () => {
    if (!canGoBackEnabled) return;
    if (index === 0) {
      setReservationSettingState((s) => ({
        ...s,
        primarySuggestionsPage: pageIndex - 1,
      }));
    }
  };

  useEffect(() => {
    if (!suggestions) return;
    if (selectedClients.length === 0) return;
    // Only clear selection if the existing one disappeared from the new suggestions list
    setSelectedClients((prev) => {
      const next = prev.map((client, i) => {
        if (!(index === 0 || (index !== 0 && i === index))) return client;
        const sel = client.selectedSuggestion;
        if (!sel) return client; // nothing to do
        const stillExists = suggestions.some((s) => s.id === sel.id);
        return stillExists ? client : { ...client, selectedSuggestion: null };
      });
      return next;
    });
  }, [suggestions, index, setSelectedClients, selectedClients.length]);

  return (
    <Grid size={selectedClients.length <= 1 ? 12 : "grow"}>
      <Grid container>
        {selectedClients[index]?.currentAppointment && (
          <Grid size={12}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
                mb: 2,
              }}
            >
              <Box>
                <Box sx={{ display: "inline-block" }}>
                  <Typography variant="h3">Přeobjednávaný termín:</Typography>
                  <Divider sx={{ mt: 1.5 }} />
                </Box>
              </Box>
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}
              >
                <Typography variant="h4" sx={{ color: "primary.main" }}>
                  {f.longDateTime(
                    new Date(selectedClients[index].currentAppointment.start),
                  )}
                </Typography>
              </Box>
            </Box>
          </Grid>
        )}
        <Grid
          size={
            selectedClients.length <= 1
              ? reservationSettingState.mode !== RESERVATION_MODES.RESCHEDULE &&
                reservationSettingState.anyClientHasNote
                ? 4
                : 6
              : 12
          }
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: "0.8rem",
              alignItems: "flex-end",
              mb: "1rem",
            }}
          >
            <Box sx={{ flex: "0 0 80%" }}>
              <ClientAutocomplete
                search={clientOptions.search}
                loading={clientOptions.loading}
                options={clientOptions.options.filter((c) =>
                  selectedClients.every((sc) => sc.selectedClient?.id !== c.id),
                )}
                option={selectedClient}
                disabled={isPrimaryClient}
                onChange={(client) => {
                  setSelectedClient(client);
                  const clientLocation =
                    locationOptions.options.find(
                      (o) => o.id === client?.location_id,
                    ) ?? null;
                  setSelectedLocation(clientLocation);
                  setSelectedDoctors(doc.getPreferredDoctors(client));
                }}
              />
            </Box>
            {!isPrimaryClient && (
              <Button
                variant="contained"
                sx={{
                  borderRadius: 1,
                  color: "white",
                  width: "2.1rem",
                  height: "2.1rem",
                  fontSize: "1.2rem",
                  minWidth: 0,
                  px: 0,
                  mb: 1,
                }}
                color="error"
                onClick={() =>
                  setSelectedClients((prev) =>
                    prev.filter((_, i) => i !== index),
                  )
                }
              >
                <FontAwesomeIcon icon={faXmark} />
              </Button>
            )}
            {reservationSettingState.mode !== RESERVATION_MODES.RESCHEDULE &&
              selectedClients.length < 4 && (
                <Button
                  variant="contained"
                  sx={{
                    display: "inline-flex",
                    borderRadius: 1,
                    color: "white",
                    width: "2.1rem",
                    height: "2.1rem",
                    fontSize: "1.2rem",
                    minWidth: 0,
                    px: 0,
                    mb: 1,
                  }}
                  onClick={() => {
                    setSelectedClients((prev) => {
                      return [
                        ...prev,
                        {
                          selectedClient: null,
                          selectedLocation: null,
                          selectedProduct: null,
                          selectedService: null,
                          selectedDoctors: doc.selectDoctors([]),
                          id: crypto.randomUUID(),
                        },
                      ];
                    });
                  }}
                >
                  <FontAwesomeIcon icon={faPlus} />
                </Button>
              )}
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
            }}
          >
            <Box
              sx={{
                flex: "0 0 80%",
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
              }}
            >
              <AutocompleteSelect
                required
                loading={locationOptions.loading}
                options={locationOptions.options}
                label={t("resources.location")}
                disabled={!isPrimaryClient}
                value={selectedLocation}
                warningText={
                  selectedClient &&
                  selectedClient.location_id &&
                  selectedLocation &&
                  selectedLocation.id !== selectedClient.location_id
                    ? "Kmenová klinika klienta je jiná."
                    : undefined
                }
                onChange={(value) => {
                  userChangedLocationRef.current = true;
                  setSelectedLocation(value);
                }}
              />
              <AutocompleteSelect
                required
                loading={productOptions.loading}
                options={productOptions.options}
                label={t("appointment.product")}
                disabled={
                  reservationSettingState.mode === RESERVATION_MODES.RESCHEDULE
                }
                value={selectedProduct}
                onChange={(value) => {
                  setSelectedProduct(value);
                  setSelectedService(null); // Reset service when product changes
                }}
              />
              <AutocompleteSelect
                required
                name="service_id"
                loading={serviceOptions.loading}
                options={serviceOptions.options}
                label={t("appointment.service")}
                disabled={
                  reservationSettingState.mode === RESERVATION_MODES.RESCHEDULE
                }
                value={selectedService}
                onChange={(value) => {
                  userChangedServiceRef.current = true;
                  setSelectedService(value);
                }}
              />
              <DoctorsInput
                doctorOptions={doctorOptions}
                selectedDoctors={selectedDoctors}
                selectedService={selectedService}
                setSelectedDoctors={setSelectedDoctors}
              />
              <FormGroup>
                <FormControlLabel
                  disabled={selectedService?.requires_doctor}
                  control={
                    <Checkbox
                      checked={
                        selectedClient?.with_selected_doctor_only ?? false
                      }
                      onChange={(_, checked) => {
                        if (selectedService?.requires_doctor) {
                          return;
                        }

                        if (checked && doc.isEmpty(selectedDoctors)) {
                          setSelectedDoctors(doc.selectAnyDoctor());
                        }
                        setSelectedClient((prev) => ({
                          ...prev,
                          with_selected_doctor_only: checked,
                        }));
                      }}
                    />
                  }
                  label="Pouze termíny s lékařem"
                />
              </FormGroup>
              <Box
                sx={{ display: "flex", gap: "1rem", flexDirection: "column" }}
              >
                <span>
                  <strong>Poznámka k termínu:</strong>
                </span>
                <TextField
                  multiline
                  rows={3}
                  sx={{ flexGrow: 1 }}
                  placeholder="Zde můžete napsat zprávu"
                  onChange={(e) => {
                    const value = e.currentTarget.value;
                    setSelectedClient((prev) => ({
                      ...prev,
                      note: value,
                    }));
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Grid>
        <Grid
          size={
            selectedClients.length <= 1
              ? reservationSettingState.mode !== RESERVATION_MODES.RESCHEDULE &&
                note &&
                oldAppointment
                ? 4
                : 0
              : 12
          }
          sx={{
            my: selectedClients.length <= 1 ? 0 : 4,
            minHeight: reservationSettingState.anyClientHasNote ? "18rem" : 0,
          }}
        >
          {reservationSettingState.mode !== RESERVATION_MODES.RESCHEDULE &&
            note &&
            oldAppointment && (
              <Box display="flex" flexDirection="column" gap="1rem">
                <Box>
                  <Typography variant="h3" sx={{ display: "inline-block" }}>
                    {t("calendar.orderClientOn")}:
                    <Divider sx={{ mt: 1.5 }} />
                  </Typography>
                </Box>

                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}
                >
                  <Typography>
                    <strong>Úkon: </strong>
                    {(() => {
                      // Prefer service referenced in receptionist note if available
                      if (note?.service_id) {
                        const noteService = serviceOptions?.options?.find(
                          (s) => s.id === note.service_id,
                        );
                        if (noteService) return noteService.name;
                      }
                      return defaultOptions[index]?.service?.name;
                    })()}
                  </Typography>
                  <Typography>
                    <strong>Období: </strong>
                    {note.reservation_count}{" "}
                    {t(`appointmentNotes.${note.reservation_unit}`)}
                  </Typography>
                  <Typography>
                    <strong>Poznámka pro recepci: </strong>
                    {note.note_content}
                  </Typography>
                  <Typography>
                    <strong>Poslední app: </strong>
                    {oldAppointment?.start
                      ? f.date(oldAppointment.start)
                      : ""}{" "}
                    - {oldAppointment?.service?.name ?? ""}
                  </Typography>
                  <Typography>
                    <strong>Nutná přítomnost lékaře: </strong>
                    {note?.doctor_required ? "Ano" : "Ne"}
                  </Typography>
                </Box>
              </Box>
            )}
        </Grid>

        <Grid size={selectedClients.length <= 1 ? 4 : 12}>
          {selectedClients.length > 1 &&
            selectedClients.filter(
              (client) =>
                client?.selectedService?.id ===
                "dad9a0a6-e0f2-429d-ae14-d1f6afdea397",
            ).length === selectedClients.length && (
              <Box sx={{ minHeight: "5rem", mt: "-1rem" }}>
                {isPrimaryClient && (
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          value={reservationSettingState.isJointConsultation}
                          onChange={(event) => {
                            const value = event.target.checked;
                            setReservationSettingState((setting) => ({
                              ...setting,
                              isJointConsultation: value,
                            }));
                            setSelectedClients((prev) => {
                              if (!prev.length) return prev;
                              return prev.map((c) => ({
                                ...c,
                                selectedSuggestion: null,
                              }));
                            });
                          }}
                        />
                      }
                      label="Sloučit konzultace"
                    />
                  </FormGroup>
                )}
              </Box>
            )}
          <Box sx={{ mb: 3 }}>
            <Box>
              <Typography variant="h3" sx={{ display: "inline-block" }}>
                {t("calendar.availableDates")}
                <Divider sx={{ mt: 1.5 }} />
              </Typography>
            </Box>
            {!suggestions.length &&
              !currentSuggestions.length &&
              !isSuggestionsLoading && (
                <Typography variant="body1">
                  Pro zobrazení termínů vyberte službu
                </Typography>
              )}
            {isSuggestionsLoading && !currentSuggestions.length && (
              <CircularProgress sx={{ m: 4 }} />
            )}
            {isPrimaryClient && currentSuggestions && (
              <FormControl required disabled={isSuggestionsLoading}>
                <RadioGroup
                  onChange={(_, value) => {
                    const sel = currentSuggestions.find((s) => s.id === value);
                    if (
                      isPrimaryClient &&
                      reservationSettingState.isDateManuallySelected &&
                      clearManualSelection
                    ) {
                      clearManualSelection();
                    }
                    if (isPrimaryClient) {
                      setSelectedClients((prev) => {
                        if (!prev.length) return prev;
                        const next = [...prev];
                        next[0] = { ...next[0], selectedSuggestion: sel };
                        return next;
                      });
                    }
                  }}
                >
                  {currentSuggestions.map((s) => {
                    const label = (
                      <>
                        {f.longDateTime(s.start)}
                        {note &&
                          !!note.reservation_count &&
                          s.generationRound > 1 && (
                            <>
                              {" "}
                              - <FontAwesomeIcon icon={faCalendarPlus} />
                            </>
                          )}
                        <DoctorIcon
                          selectedDoctors={selectedDoctors}
                          availableEmployees={s.availableEmployees}
                        />
                      </>
                    );

                    return (
                      <FormControlLabel
                        key={s.id}
                        value={s.id}
                        control={
                          <Radio
                            checked={
                              s.id ===
                              selectedClients[index]?.selectedSuggestion?.id
                            }
                          />
                        }
                        label={label}
                      />
                    );
                  })}
                </RadioGroup>
              </FormControl>
            )}
            {!isPrimaryClient &&
              !reservationSettingState.isJointConsultation &&
              selectedClients[0].selectedSuggestion &&
              suggestions && (
                <FormControl
                  required
                  disabled={isSuggestionsLoading}
                  sx={{
                    height: "16rem",
                    overflowY: "scroll",
                    width: "100%",
                  }}
                >
                  <RadioGroup
                    onChange={(_, value) => {
                      const sel = suggestions.find((s) => s.id === value);
                      setSelectedClients((prev) =>
                        prev.map((c, i) =>
                          i === index ? { ...c, selectedSuggestion: sel } : c,
                        ),
                      );
                    }}
                  >
                    {nonOverlappingSuggestions
                      .filter(
                        (suggestion) =>
                          suggestion.start.split(" ")[0] ===
                          selectedClients[0].selectedSuggestion.start.split(
                            " ",
                          )[0],
                      )
                      .sort((a, b) => a.start.localeCompare(b.start))
                      .map((s) => {
                        const label = (
                          <>
                            {f.time24(s.start)}
                            {note &&
                              !!note.reservation_count &&
                              s.generationRound > 1 && (
                                <>
                                  {" "}
                                  - <FontAwesomeIcon icon={faCalendarPlus} />
                                </>
                              )}
                          </>
                        );

                        return (
                          <FormControlLabel
                            key={s.id}
                            value={s.id}
                            control={
                              <Radio
                                checked={
                                  s.id ===
                                  selectedClients[index]?.selectedSuggestion?.id
                                }
                              />
                            }
                            label={label}
                          />
                        );
                      })}
                  </RadioGroup>
                </FormControl>
              )}
            {!isPrimaryClient &&
              reservationSettingState.isJointConsultation && (
                <span>Společná konzultace</span>
              )}
          </Box>
          {selectedClients[index]?.selectedSuggestion && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
                mb: 2,
              }}
            >
              <Box>
                <Box sx={{ display: "inline-block" }}>
                  <Typography variant="h3">Aktuálně vybraný termín:</Typography>
                  <Divider sx={{ mt: 1.5 }} />
                </Box>
              </Box>
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}
              >
                <Typography variant="h4" sx={{ color: "primary.main" }}>
                  {f.longDateTime(
                    new Date(selectedClients[index]?.selectedSuggestion?.start),
                  )}
                  {selectedService && (
                    <span style={{ color: "text.primary" }}>
                      {" - "}
                      {selectedService.name}
                    </span>
                  )}
                </Typography>
              </Box>
            </Box>
          )}
          {isPrimaryClient && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: "1rem",
                mb: 3,
              }}
            >
              <Button
                sx={{ padding: "0.7rem 2.4rem", gap: "0.6rem" }}
                disabled={!canGoBackEnabled}
                onClick={handleBackSuggestions}
                variant="outlined"
              >
                <FontAwesomeIcon icon={faRotateLeft} />
                {t("form.back")}
              </Button>
              <Button
                disabled={!hasMoreEnabled}
                sx={{ padding: "0.7rem 2.4rem", gap: "0.6rem" }}
                onClick={handleNextSuggestions}
                variant="outlined"
              >
                <FontAwesomeIcon icon={faArrowsRotate} />
                {t("form.next")}
              </Button>
            </Box>
          )}
          {isPrimaryClient &&
            selectedClients[0].selectedClient &&
            selectedClients[0].selectedLocation &&
            selectedClients[0].selectedProduct &&
            selectedClients[0].selectedService &&
            openManualDialog && (
              <Typography
                sx={{
                  display: "flex",
                  gap: "0.6rem",
                  cursor: "pointer",
                  mb: 3,
                  mt: 2,
                }}
                variant="h4"
                onClick={() => openManualDialog()}
              >
                {t("calendar.manualTimeSelection")}
                <FontAwesomeIcon icon={faCalendarLinesPen} />
              </Typography>
            )}
        </Grid>
      </Grid>
    </Grid>
  );
};
